import * as React from "react";
import {
  crearClienteAuth,
  entrarConGoogle,
  salir,
  tokenActual,
  type ClienteAuth,
} from "@iavmx/auth/navegador";
import type { PersonaSesion } from "@iavmx/auth";
import { crearApi, leer, type Api } from "./api";

export interface Configuracion {
  vista: "v360" | "semaforo";
  supabaseUrl: string | null;
  supabaseAnonKey: string | null;
  urlPortal: string;
  /** Solo en local con VISTA_LOCAL: la API actúa como Nailea Aguirre sin token. */
  modoLocal?: boolean;
}

interface Sesion {
  cargando: boolean;
  configuracion: Configuracion | null;
  correo: string | null;
  /** La persona de nucleo.personas detrás de la sesión (o del modo local). */
  persona: PersonaSesion | null;
  api: Api;
  entrar: () => Promise<void>;
  cerrar: () => Promise<void>;
}

const Contexto = React.createContext<Sesion | null>(null);

// Un solo cliente de Auth por pestaña; el API lo lee al pedir el token, no durante el render.
let clienteActual: ClienteAuth | null = null;
const api: Api = crearApi(async () => (clienteActual ? tokenActual(clienteActual) : null));

export function SesionProveedor({ children }: { children: React.ReactNode }) {
  const [configuracion, setConfiguracion] = React.useState<Configuracion | null>(null);
  const [cliente, setCliente] = React.useState<ClienteAuth | null>(null);
  const [correo, setCorreo] = React.useState<string | null>(null);
  const [persona, setPersona] = React.useState<PersonaSesion | null>(null);
  const [cargando, setCargando] = React.useState(true);

  // Con sesión (o en modo local) la API dice quién es la persona del directorio.
  React.useEffect(() => {
    if (!configuracion) return;
    // Sin sesión ni modo local no hay a quién preguntar; la persona visible se deriva abajo.
    if (!correo && !configuracion.modoLocal) return;
    let cancelado = false;
    api.api.yo
      .$get()
      .then((r) => leer<PersonaSesion>(r))
      .then((p) => {
        if (cancelado) return;
        setPersona(p);
        if (!correo) setCorreo(p.correo);
      })
      .catch(() => {
        if (!cancelado) setPersona(null);
      });
    return () => {
      cancelado = true;
    };
  }, [configuracion, correo]);

  React.useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const c = await leer<Configuracion>(
          await fetch(`/api/configuracion${window.location.search}`),
        );
        if (cancelado) return;
        setConfiguracion(c);
        if (c.supabaseUrl && c.supabaseAnonKey) {
          const cl = crearClienteAuth(c.supabaseUrl, c.supabaseAnonKey);
          clienteActual = cl;
          setCliente(cl);
          const { data } = await cl.auth.getSession();
          setCorreo(data.session?.user.email ?? null);
          cl.auth.onAuthStateChange((_e, s) => setCorreo(s?.user.email ?? null));
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelado) setCargando(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  // Al cerrar sesión la persona cargada deja de ser visible sin tocar el estado desde un efecto.
  const personaVisible = correo || configuracion?.modoLocal ? persona : null;
  const valor = React.useMemo<Sesion>(
    () => ({
      cargando,
      configuracion,
      correo,
      persona: personaVisible,
      api,
      entrar: async () => {
        if (!cliente) throw new Error("Auth sin configurar.");
        await entrarConGoogle(cliente, window.location.origin);
      },
      cerrar: async () => {
        if (cliente) await salir(cliente);
      },
    }),
    [cargando, configuracion, correo, personaVisible, cliente],
  );
  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useSesion(): Sesion {
  const s = React.useContext(Contexto);
  if (!s) throw new Error("useSesion necesita <SesionProveedor>.");
  return s;
}
